import { useState, useEffect, useCallback, useMemo, useRef } from 'react'

import { create, update, bulkUpdate, deleteOne, getMany, getManyRaw } from "../../../../api/TemplateAPI"
import * as LookUpAPI from '../../../../api/LookUpAPI';
import * as PhysiciansAPI from '../../../../api/PhysiciansAPI'
import { message } from 'antd'
import { abbrToName, useStates } from '../../../../hooks/useStates'
import { TemplateTypeEnum } from '../../../../constants/enums'

const NON_PRESCRIPTION_TYPES = [TemplateTypeEnum.CASE, TemplateTypeEnum.MESSAGE, TemplateTypeEnum.VISIT]

const defaultFilters = {
    owner: null,
    categoryIds: null,
    status: null,
    search: null,
    pharmacyId: null,
    medicationName: null,
    ndc: null,
    states: [],
    sortBy: 'title',
    sortDirection: 'asc',
}

const getValueByPath = (obj, path) => path.split('.').reduce((acc, key) => acc?.[key], obj)

const escapeRegex = str => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const applyPrescriptionFilters = (templates, filters) => {
    const { search, categoryIds, pharmacyId, medicationName, ndc, states, sortBy, sortDirection } = filters

    let result = templates.filter(template => {
        if (search && !new RegExp(escapeRegex(search), 'i').test(template.title)) return false
        if (categoryIds && !template.teleMedicineCategoryIds?.some(c => c._id === categoryIds)) return false
        if (pharmacyId && template.pharmacyId !== pharmacyId) return false
        if (medicationName?.length >= 3 && !new RegExp(escapeRegex(medicationName), 'i').test(template.medication?.drugName)) return false
        if (ndc?.length === 11 && !template.medication?.pseudonyms?.includes(ndc)) return false
        if (states?.length > 0) {
            const selectedNames = states.map(abbrToName)
            if (!template.states?.some(s => selectedNames.includes(s.name?.toLowerCase()))) return false
        }
        return true
    })

    if (sortBy) {
        result = [...result].sort((a, b) => {
            const aVal = getValueByPath(a, sortBy) ?? ''
            const bVal = getValueByPath(b, sortBy) ?? ''
            const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
            return sortDirection === 'desc' ? -cmp : cmp
        })
    }

    return result
}

const mapTemplates = templates => templates.map(template => ({
    ...template,
    teleMedicineCategories: template.teleMedicineCategoryIds?.map(c => c.name).join(', '),
    ...(template.statusId && { caseStatus: template.statusId.name })
}))

export function useTemplates(type) {
    const [loadingByType, setLoadingByType] = useState(
        Object.fromEntries(
            [...NON_PRESCRIPTION_TYPES, TemplateTypeEnum.PRESCRIPTION].map(t => [t, false])
        )
    )
    const [selectedTemplate, selectTemplate] = useState(null)
    const [templatesByType, setTemplatesByType] = useState(
        Object.fromEntries(
            [...NON_PRESCRIPTION_TYPES, TemplateTypeEnum.PRESCRIPTION].map(t => [t, []])
        )
    )
    const [telemedicineCategories, setTelemedicineCategories] = useState([])
    const [caseStatuses, setCaseStatues] = useState([])
    const [physicians, setPhysicians] = useState([])
    const [pharmacyOptions, setPharmacyOptions] = useState([]);
    const { stateOptions } = useStates(templatesByType[TemplateTypeEnum.PRESCRIPTION]);

    // null = effect has not run yet (initial mount).
    const prevTypeRef = useRef(null)
    // Stable ref to the current type so callbacks don't need type in their dep arrays.
    const typeRef = useRef(type)
    // Tracks whether a prescription fetch has ever been initiated so the guard
    // effect doesn't re-fire after deleting the last template (length → 0).
    const prescriptionFetchedRef = useRef(false)
    useEffect(() => { typeRef.current = type }, [type])

    // All tabs persist their own filter state — no reset on tab switch.
    const [filtersByType, setFiltersByType] = useState(
        Object.fromEntries(
            [...NON_PRESCRIPTION_TYPES, TemplateTypeEnum.PRESCRIPTION].map(t => [t, { ...defaultFilters }])
        )
    )

    const templateFilters = filtersByType[type]

    // Stable — typeRef provides the current type without adding it to the dep array.
    const setTemplateFilters = useCallback(update => {
        const t = typeRef.current
        setFiltersByType(prev => ({
            ...prev,
            [t]: typeof update === 'function' ? update(prev[t]) : update
        }))
    }, [])

    // Computed synchronously during render — no setState/useEffect cycle needed
    const prescriptionFilters = filtersByType[TemplateTypeEnum.PRESCRIPTION]
    const allPrescriptionTemplates = templatesByType[TemplateTypeEnum.PRESCRIPTION]
    const filteredPrescriptionTemplates = useMemo(
        () => applyPrescriptionFilters(allPrescriptionTemplates, prescriptionFilters),
        [allPrescriptionTemplates, prescriptionFilters]
    )

    // Only expose categories that have at least one prescription template.
    const categoryOptions = useMemo(() => {
        const seen = new Set()
        return allPrescriptionTemplates
            .flatMap(t => t.teleMedicineCategoryIds ?? [])
            .filter(c => {
                if (!c?._id || seen.has(c._id.toString())) return false
                seen.add(c._id.toString())
                return true
            })
    }, [allPrescriptionTemplates])

    useEffect(() => {
        Promise.all([
            LookUpAPI.lookUp('tele-medicine-category'),
            LookUpAPI.lookUp('case-status'),
            PhysiciansAPI.getPhysiciansV2(),
        ])
            .then(([categoriesRes, statusesRes, physiciansRes]) => {
                setTelemedicineCategories(categoriesRes.data.payload)
                setCaseStatues(statusesRes.data.payload)
                setPhysicians(physiciansRes.data.physicians)
            })
            .catch(e => {
                console.error(e)
                message.error('Failed to load reference data')
            })
    }, [])

    // Fetch all prescription templates once, derive option lists, and store the full set.
    // Filters are applied client-side against this list — no subsequent API calls needed.
    const fetchAllPrescriptionTemplates = useCallback(async () => {
        prescriptionFetchedRef.current = true
        setLoadingByType(prev => ({ ...prev, [TemplateTypeEnum.PRESCRIPTION]: true }))
        try {
            const { data: { templates } } = await getManyRaw({ type: TemplateTypeEnum.PRESCRIPTION })

            if (!templates?.length) {
                setTemplatesByType(prev => ({ ...prev, [TemplateTypeEnum.PRESCRIPTION]: [] }))
                return
            }

            const mapped = mapTemplates(templates)

            const seenPharmacies = new Set()
            setPharmacyOptions(
                mapped
                    .filter(t => t.pharmacyId && t.pharmacyName)
                    .filter(t => {
                        if (seenPharmacies.has(t.pharmacyId)) return false
                        seenPharmacies.add(t.pharmacyId)
                        return true
                    })
                    .map(t => ({ id: t.pharmacyId, name: t.pharmacyName }))
            )

            setTemplatesByType(prev => ({ ...prev, [TemplateTypeEnum.PRESCRIPTION]: mapped }))
        } catch (e) {
            console.error(e)
            message.error('Failed to load prescription templates')
        } finally {
            setLoadingByType(prev => ({ ...prev, [TemplateTypeEnum.PRESCRIPTION]: false }))
        }
    }, [])

    // Non-cancellable initial load for a single non-prescription type.
    // Stores results in templatesByType[targetType] so tab switches are instant.
    const fetchTemplatesForType = useCallback(async targetType => {
        setLoadingByType(prev => ({ ...prev, [targetType]: true }))
        try {
            const { data: { templates } } = await getManyRaw({ type: targetType })
            setTemplatesByType(prev => ({
                ...prev,
                [targetType]: templates?.length ? mapTemplates(templates) : []
            }))
        } catch (e) {
            console.error(e)
            message.error(`Failed to load ${targetType} templates`)
        } finally {
            setLoadingByType(prev => ({ ...prev, [targetType]: false }))
        }
    }, [])

    // Pre-fetch all template types on mount so every tab is instant on first click.
    useEffect(() => {
        fetchAllPrescriptionTemplates()
        NON_PRESCRIPTION_TYPES.forEach(t => fetchTemplatesForType(t))
    }, [])

    // If the user arrives on the prescription tab before the mount pre-fetch fires
    // (concurrent mode edge case), kick off a fetch. prescriptionFetchedRef prevents
    // this from re-triggering after the last template is deleted (length → 0).
    useEffect(() => {
        if (type === TemplateTypeEnum.PRESCRIPTION && !prescriptionFetchedRef.current) {
            fetchAllPrescriptionTemplates()
        }
    }, [type, fetchAllPrescriptionTemplates])

    // Cancellable search for non-prescription tabs — used for live filter changes and
    // post-save refreshes. Type is read from params.type so no ref is needed.
    const searchTemplates = useCallback(async params => {
        const t = params.type
        setLoadingByType(prev => ({ ...prev, [t]: true }))
        setTemplatesByType(prev => ({ ...prev, [t]: [] }))

        try {
            const { data: { templates } } = await getMany(params)
            setTemplatesByType(prev => ({
                ...prev,
                [t]: templates?.length ? mapTemplates(templates) : []
            }))
        } catch (e) {
            if (e?.name === 'CanceledError') return
            console.log(e)
        } finally {
            setLoadingByType(prev => ({ ...prev, [t]: false }))
        }
    }, [])

    // Fires on type or filter changes. Guards keep searches user-driven only:
    // - initial mount is skipped (pre-fetch covers it)
    // - tab switches are skipped (pre-fetched data is already in templatesByType)
    // - prescription tab is always skipped (filtered client-side)
    useEffect(() => {
        const prevType = prevTypeRef.current
        prevTypeRef.current = type

        if (prevType === null) return                       // initial mount
        if (type === TemplateTypeEnum.PRESCRIPTION) return  // client-side filtering
        if (prevType !== type) return                       // tab switch — use pre-fetched data

        searchTemplates({ type, ...templateFilters })
    }, [type, templateFilters]) // eslint-disable-line react-hooks/exhaustive-deps

    // The create/update API responses return teleMedicineCategoryIds as unpopulated
    // ObjectIds. Resolve them from telemedicineCategories already in state so incremental
    // updates display correctly without triggering a full collection re-fetch.
    const resolveCategories = useCallback(ids =>
        (ids ?? [])
            .map(id => {
                const idStr = id?._id?.toString() ?? id?.toString()
                return telemedicineCategories.find(c => c._id?.toString() === idStr)
            })
            .filter(Boolean),
    [telemedicineCategories])

    // Builds a mapped prescription template from a raw API response, resolving
    // category names from local state. Shared by createTemplate and editTemplate.
    const mapPrescriptionResponse = (raw, teleMedicineCategoryIds) =>
        mapTemplates([{
            ...raw,
            teleMedicineCategoryIds: resolveCategories(teleMedicineCategoryIds),
        }])[0]

    const addPharmacyOptionIfNew = useCallback((pharmacyId, pharmacyName) => {
        if (!pharmacyId || !pharmacyName) return
        setPharmacyOptions(prev =>
            prev.some(p => p.id === pharmacyId)
                ? prev
                : [...prev, { id: pharmacyId, name: pharmacyName }]
        )
    }, [])

    const createTemplate = async ({ 
        title, 
        pharmacyNotes, 
        directions, 
        teleMedicineCategoryIds,
        DAW,
        pharmacyId,
        pharmacyName,
        states,
        subtype,
        ...medication 
    }) => {
        try {
            const { data: { template: raw } } = await create(type, {
                title,
                pharmacyNotes,
                directions,
                teleMedicineCategoryIds,
                DAW,
                pharmacyId,
                pharmacyName,
                states,
                subtype,
                medication
            });
            message.success('Template successfully created')
            if (type === TemplateTypeEnum.PRESCRIPTION) {
                const mapped = mapPrescriptionResponse(raw, teleMedicineCategoryIds)
                setTemplatesByType(prev => ({
                    ...prev,
                    [TemplateTypeEnum.PRESCRIPTION]: [mapped, ...prev[TemplateTypeEnum.PRESCRIPTION]]
                }))
                addPharmacyOptionIfNew(pharmacyId, pharmacyName)
            } else {
                searchTemplates({ type, ...templateFilters })
            }
        } catch (e) {
            console.log(e)
            message.error('Error creating template')
        }
    }

    const editTemplate = async (
        templateId, { 
            title, 
            pharmacyNotes, 
            directions, 
            DAW,
            teleMedicineCategoryIds,
            pharmacyId,
            pharmacyName,
            states,
            subtype,
            ...medication 
        }
    ) => {
        try {
            const { data: { template: raw } } = await update(templateId, {
                templateId,
                title,
                medication,
                pharmacyNotes,
                directions,
                DAW,
                teleMedicineCategoryIds,
                pharmacyId,
                pharmacyName,
                states,
                subtype,
            })

            message.success('Successfully updated template')
            if (type === TemplateTypeEnum.PRESCRIPTION) {
                const mapped = mapPrescriptionResponse(raw, teleMedicineCategoryIds)
                setTemplatesByType(prev => ({
                    ...prev,
                    [TemplateTypeEnum.PRESCRIPTION]: prev[TemplateTypeEnum.PRESCRIPTION].map(
                        t => t._id === templateId ? mapped : t
                    )
                }))
                addPharmacyOptionIfNew(pharmacyId, pharmacyName)
            } else {
                searchTemplates({ type, ...templateFilters })
            }
        } catch (e) {
            console.error(e)
            message.error('Error editing template')
        }
    }

    const bulkEditTemplates = async (templateIds, updates) => {
        try {
            const { data: { templates: rawTemplates, warning } } = await bulkUpdate({ templateIds, updates })

            const count = rawTemplates.length
            message.success(`Successfully updated ${count} template${count !== 1 ? 's' : ''}`)
            if (warning) message.warning(warning)

            const mappedById = {}
            rawTemplates.forEach(raw => {
                mappedById[raw._id] = mapTemplates([raw])[0]
            })

            if (Object.keys(mappedById).length > 0) {
                setTemplatesByType(prev => ({
                    ...prev,
                    [TemplateTypeEnum.PRESCRIPTION]: prev[TemplateTypeEnum.PRESCRIPTION].map(
                        t => mappedById[t._id] ?? t
                    )
                }))
            }

            return true
        } catch (e) {
            console.error(e)
            message.error('Error bulk updating templates')
            return false
        }
    }

    const deleteTemplate = useCallback(async templateId => {
        try {
            await deleteOne(templateId);
            const t = typeRef.current
            setTemplatesByType(prev => ({
                ...prev,
                [t]: prev[t].filter(p => p._id !== templateId)
            }))
        } catch (e) {
            console.error(e)
            throw new Error(`Error deleting template`)
        }
    }, []) // typeRef is always current — stable across tab switches

    return {
        selectTemplate,
        createTemplate,
        editTemplate,
        bulkEditTemplates,
        deleteTemplate,
        setTemplateFilters,
        searchTemplates,
        telemedicineCategories,
        caseStatuses,
        physicians,
        pharmacyOptions,
        categoryOptions,
        stateOptions,
        type,
        selectedTemplate,
        loading: loadingByType[type] ?? false,
        loadingByType,
        templatesByType,
        filtersByType,
        templates: type === TemplateTypeEnum.PRESCRIPTION ? filteredPrescriptionTemplates : (templatesByType[type] ?? []),
        templateFilters
    };
}
